<template>
  <div class="growth-card">
    <div class="growth-head">
      <p class="growth-title">Portfolio Growth</p>
      <p class="growth-current">
        Current value
        <span class="growth-current-value">{{ currentLabel }}</span>
      </p>
    </div>

    <!-- Chart area -->
    <div class="chart-wrap" @mousemove="onMove" @mouseleave="hoverIndex = null">
      <!-- Loading -->
      <div v-if="loading" class="chart-state">
        <span class="spinner"></span>
      </div>

      <!-- Ghost (fewer than 2 points) -->
      <template v-else-if="!hasSeries">
        <svg class="chart-svg" viewBox="0 0 300 180" preserveAspectRatio="none">
          <path
            :d="ghostPath"
            fill="none"
            stroke="#D0D5E0"
            stroke-width="3"
            vector-effect="non-scaling-stroke"
            stroke-linecap="round"
          />
        </svg>
        <div class="ghost-pill">No investments yet</div>
      </template>

      <!-- Real chart -->
      <template v-else>
        <svg class="chart-svg" viewBox="0 0 300 180" preserveAspectRatio="none">
          <defs>
            <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#4E84FF" stop-opacity="0.25" />
              <stop offset="100%" stop-color="#4E84FF" stop-opacity="0" />
            </linearGradient>
          </defs>

          <!-- Gridlines -->
          <line
            v-for="(g, i) in gridlines"
            :key="i"
            x1="6"
            :y1="g"
            x2="294"
            :y2="g"
            stroke="#EEF1F6"
            stroke-width="1"
            vector-effect="non-scaling-stroke"
          />

          <!-- Fill -->
          <path :d="fillPath" :fill="`url(#${gradientId})`" />
          <!-- Line -->
          <path
            :d="linePath"
            fill="none"
            stroke="#4E84FF"
            stroke-width="3"
            vector-effect="non-scaling-stroke"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <!-- Y axis labels -->
        <div class="y-labels">
          <span v-for="(lbl, i) in yLabels" :key="i" class="y-label" :style="{ top: lbl.top + '%' }">
            {{ lbl.text }}
          </span>
        </div>

        <!-- Hover indicator + tooltip -->
        <template v-if="hoverPoint">
          <div class="hover-line" :style="{ left: hoverPoint.leftPct + '%' }"></div>
          <div class="hover-dot" :style="{ left: hoverPoint.leftPct + '%', top: hoverPoint.topPct + '%' }"></div>
          <div class="tooltip" :style="{ left: hoverPoint.leftPct + '%' }">
            {{ tooltipLabel }}
          </div>
        </template>
      </template>
    </div>

    <!-- X axis end labels -->
    <div class="x-labels">
      <span>{{ hasSeries ? agoLabel : "" }}</span>
      <span>{{ hasSeries ? "Now" : "" }}</span>
    </div>

    <!-- Period selector -->
    <div class="period-row">
      <button
        v-for="p in periods"
        :key="p.key"
        class="period-pill"
        :class="{ active: p.key === period }"
        type="button"
        @click="$emit('change-period', p.key)"
      >
        {{ p.label }}
      </button>
    </div>
  </div>
</template>

<script>
import * as inv from "@/utils/invest.js";

const W = 300;
const H = 180;
const PAD_X = 6;
const PAD_TOP = 16;
const PAD_BOTTOM = 14;
const PLOT_W = W - PAD_X * 2;
const PLOT_H = H - PAD_TOP - PAD_BOTTOM;

export default {
  name: "InvestGrowthChart",
  props: {
    series: { type: Array, default: () => [] },
    currency: { type: String, default: "NGN" },
    loading: { type: Boolean, default: false },
    period: { type: String, default: "1M" },
    agoLabel: { type: String, default: "" },
    // Value shown as "Current value"; falls back to last series point.
    currentValue: { type: Number, default: 0 },
  },
  emits: ["change-period"],
  data() {
    return {
      periods: inv.PERIODS,
      hoverIndex: null,
      // Unique gradient id so multiple chart instances never collide.
      gradientId: `growth-grad-${Math.round(PLOT_W + PLOT_H)}-${Math.floor(PAD_TOP * 1000) % 997}`,
    };
  },
  computed: {
    hasSeries() {
      return Array.isArray(this.series) && this.series.length >= 2;
    },
    currentLabel() {
      return inv.money(this.currentValue, this.currency, 2);
    },
    bounds() {
      const vals = this.series;
      let min = Math.min(...vals);
      let max = Math.max(...vals);
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        min = 0;
        max = 1;
      }
      if (min === max) {
        // Flat series — pad so the line sits mid-plot.
        const pad = Math.abs(min) || 1;
        min -= pad;
        max += pad;
      }
      return { min, max };
    },
    points() {
      if (!this.hasSeries) return [];
      const n = this.series.length;
      const { min, max } = this.bounds;
      return this.series.map((v, i) => {
        const x = PAD_X + (n === 1 ? 0 : (i / (n - 1)) * PLOT_W);
        const y = PAD_TOP + (1 - (v - min) / (max - min)) * PLOT_H;
        return { x, y, v, i };
      });
    },
    linePath() {
      return this.buildSmoothPath(this.points);
    },
    fillPath() {
      if (!this.points.length) return "";
      const base = H - PAD_BOTTOM;
      const first = this.points[0];
      const last = this.points[this.points.length - 1];
      return `${this.buildSmoothPath(this.points)} L ${last.x} ${base} L ${first.x} ${base} Z`;
    },
    ghostPath() {
      // A gentle static curve for the empty state.
      const pts = [0.9, 0.7, 0.78, 0.55, 0.62, 0.4, 0.5].map((r, i, a) => ({
        x: PAD_X + (i / (a.length - 1)) * PLOT_W,
        y: PAD_TOP + r * PLOT_H,
      }));
      return this.buildSmoothPath(pts);
    },
    gridlines() {
      return [0.25, 0.5, 0.75].map((r) => PAD_TOP + r * PLOT_H);
    },
    yLabels() {
      const { min, max } = this.bounds;
      // Top → bottom at 0%, 50%, 100% of the plot band.
      return [0, 0.5, 1].map((r) => {
        const value = max - r * (max - min);
        const top = ((PAD_TOP + r * PLOT_H) / H) * 100;
        return { text: inv.compact(value), top };
      });
    },
    hoverPoint() {
      if (this.hoverIndex == null || !this.points.length) return null;
      const p = this.points[this.hoverIndex];
      if (!p) return null;
      return {
        leftPct: (p.x / W) * 100,
        topPct: (p.y / H) * 100,
        v: p.v,
      };
    },
    tooltipLabel() {
      if (!this.hoverPoint) return "";
      return inv.money(this.hoverPoint.v, this.currency, 2);
    },
  },
  methods: {
    // Catmull-Rom → cubic-bezier smoothing for a natural curve with no dots.
    buildSmoothPath(pts) {
      if (!pts.length) return "";
      if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }
      return d;
    },
    onMove(e) {
      if (!this.hasSeries) return;
      const rect = e.currentTarget.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
      this.hoverIndex = Math.round(ratio * (this.series.length - 1));
    },
  },
};
</script>

<style lang="scss" scoped>
.growth-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 8px;
  padding: 16px;

  @media (min-width: 1024px) {
    padding: 20px 22px;
  }
}

.growth-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.growth-title {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #001240;
}

.growth-current {
  margin: 0;
  font-size: 11px;
  color: #8791a7;
}

.growth-current-value {
  font-weight: 500;
  color: #001240;
}

.chart-wrap {
  position: relative;
  height: 180px;
  width: 100%;
  flex: 1;

  @media (min-width: 1024px) {
    height: 300px;
    min-height: 300px;
  }
}

.chart-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.chart-state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid #d0d5e0;
  border-top-color: #4e84ff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.ghost-pill {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 6px 14px;
  border-radius: 20px;
  background: #f7f9fd;
  border: 1px solid #e8ecf4;
  font-size: 12px;
  color: #8791a7;
}

.y-labels {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.y-label {
  position: absolute;
  left: 2px;
  transform: translateY(-50%);
  font-size: 9px;
  color: #b8c0d0;
  background: rgba(255, 255, 255, 0.7);
  padding: 0 2px;
}

.hover-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(78, 132, 255, 0.35);
  transform: translateX(-50%);
  pointer-events: none;
}

.hover-dot {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4e84ff;
  border: 2px solid #ffffff;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.tooltip {
  position: absolute;
  top: -2px;
  transform: translateX(-50%);
  padding: 3px 8px;
  border-radius: 6px;
  background: #001240;
  color: #ffffff;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
}

.x-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 10px;
  color: #8791a7;
  min-height: 12px;
}

.period-row {
  display: flex;
  gap: 6px;
  margin-top: 12px;
}

.period-pill {
  flex: 1;
  padding: 6px 0;
  border: none;
  border-radius: 8px;
  background: #f7f9fd;
  color: #8791a7;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &.active {
    background: #4e84ff;
    color: #ffffff;
  }
}
</style>
